import _ from 'lodash';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { useParams } from 'react-router';
import { NewsSummaryResponse } from '../../../response/responseSchema';
import { Hydratable } from '@type/ssrType';
import { useSsr } from '@client/ssr/SsrProvider';
import Box from '@client/uikit/Box';
import Frame from '@client/uikit/Frame';
import { MarkdownViewer } from '@client/component/editor/MarkdownViewer';
import { apiClient } from '@app/apiClient';
import { useMobxInstance } from '@client/clientUtils/mobxUtil';
import Jsonld from '../Jsonld';
import MetaHeader from '@client/component/MetaHeader';
import { CommonTimeFormat } from '@client/component/CommonDateFormat';
import modalStore from '@client/stores/modalStore';
import toastStore from '@client/stores/toastStore';
import firebaseStore from '@client/stores/firebaseStore';
import { Trash2Icon, CopyIcon, ChevronDownIcon } from 'lucide-react';
import webViewStore from '@client/stores/webViewStore';
import { useCommonNavigate } from '@client/component/CommonLink';
import { NewsCopyModal } from './NewsCopyModal';

class ViewStore implements Hydratable<'NewsDetailPage'> {
    readonly newsSummarySlug: string;

    newsSummary: NewsSummaryResponse | null = null;
    markdown: string = '';

    constructor(newsSummarySlug: string) {
        this.newsSummarySlug = newsSummarySlug;
        makeAutoObservable(this);
    }

    hydrate(data: NewsSummaryResponse) {
        apiClient.news.public.getNewsSummaryBySlug.$setCache(
            {
                newsSummarySlug: this.newsSummarySlug,
            },
            data,
        );

        this.newsSummary = data;
        this.markdown = this.generateMarkdown();
    }

    async fetch() {
        if (!this.newsSummarySlug) {
            return;
        }

        const [newsSummary] = await Promise.all([
            apiClient.news.public.getNewsSummaryBySlug.$query({
                newsSummarySlug: this.newsSummarySlug,
            }),
        ]);

        runInAction(() => {
            this.newsSummary = newsSummary;
            this.markdown = this.generateMarkdown();
        });
    }

    private generateMarkdown(): string {
        const line = [];

        if (!this.newsSummary) {
            return '';
        }

        const body = this.newsSummary.body;

        if (body.summary) {
            line.push(`### 사건`);
            _.forEach(body.summary?.event, (event) => {
                line.push(`- ${event}`);
            });
        }

        if (body.summary?.analysis) {
            line.push(`### 분석`);
            _.forEach(body.summary?.analysis, (analysis) => {
                line.push(`- ${analysis}`);
            });
        }

        if (body.summary?.market) {
            line.push(`### 시장반응`);
            _.forEach(body.summary?.market, (market) => {
                line.push(`- ${market}`);
            });
        }

        if (body.summary?.other) {
            line.push(`### 기타`);
            _.forEach(body.summary?.other, (other) => {
                line.push(`- ${other}`);
            });
        }

        if (body.additional) {
            line.push(`### 용어`);
            _.forEach(body.additional, (additional) => {
                line.push(`- ${additional}`);
            });
        }

        if (body.action) {
            line.push(`### 대응 방안`);
            _.forEach(body.action, (action) => {
                line.push(`- ${action}`);
            });
        }

        if (body.explorer) {
            line.push(`### 더 알아보기`);
            _.forEach(body.explorer, (explorer) => {
                line.push(`- ${explorer}`);
            });
        }

        return line.join('\n');
    }

    async copyTitle() {
        if (!this.newsSummary) {
            return;
        }

        try {
            await navigator.clipboard.writeText(this.newsSummary.title);
            toastStore.success('제목이 복사되었습니다');
        } catch (error) {
            toastStore.error('복사에 실패했습니다');
        }
    }

    async copyContentForBlog() {
        if (!this.newsSummary) {
            return;
        }

        const body = this.newsSummary.body;
        const textLines = [];
        const htmlLines = [];

        // 핵심요약
        if (body.explanation) {
            textLines.push(`기사 핵심요약`);
            htmlLines.push(`<h3 style="font-size: 18px; line-height: 1.8;">기사 핵심요약</h3>`);
            textLines.push(body.explanation);
            textLines.push('');
            htmlLines.push(`<p style="font-size: 19px; line-height: 1.8;">${body.explanation}</p>`);
        }

        htmlLines.push(`<p style="font-size: 16px; line-height: 1.6;"></p>`);
        htmlLines.push(`<br/><br />`);

        // 사건
        if (body.summary?.event && body.summary.event.length > 0) {
            textLines.push(`사건`);
            htmlLines.push(`<h3 style="font-size: 18px; line-height: 1.8;">사건</h3>`);
            htmlLines.push(`<ul>`);
            _.forEach(body.summary.event, (event) => {
                textLines.push(`- ${event}`);
                htmlLines.push(`<li>${event}</li>`);
            });
            htmlLines.push(`</ul>`);
            htmlLines.push(`<br/>`);
            textLines.push('');
        }

        // 분석
        if (body.summary?.analysis && body.summary.analysis.length > 0) {
            textLines.push(`분석`);
            htmlLines.push(`<h3 style="font-size: 18px; line-height: 1.8;">분석</h3>`);
            htmlLines.push(`<ul>`);
            _.forEach(body.summary.analysis, (analysis) => {
                textLines.push(`- ${analysis}`);
                htmlLines.push(`<li>${analysis}</li>`);
            });
            htmlLines.push(`</ul>`);
            htmlLines.push(`<br/>`);
            textLines.push('');
        }

        // 시장반응
        if (body.summary?.market && body.summary.market.length > 0) {
            textLines.push(`시장반응`);
            htmlLines.push(`<h3 style="font-size: 18px; line-height: 1.8;">시장반응</h3>`);
            htmlLines.push(`<ul>`);
            _.forEach(body.summary.market, (market) => {
                textLines.push(`- ${market}`);
                htmlLines.push(`<li>${market}</li>`);
            });
            htmlLines.push(`</ul>`);
            htmlLines.push(`<br/>`);
            textLines.push('');
        }

        // 기타
        if (body.summary?.other && body.summary.other.length > 0) {
            textLines.push(`기타`);
            htmlLines.push(`<h3 style="font-size: 18px; line-height: 1.8;">기타</h3>`);
            htmlLines.push(`<ul>`);
            _.forEach(body.summary.other, (other) => {
                textLines.push(`- ${other}`);
                htmlLines.push(`<li>${other}</li>`);
            });
            htmlLines.push(`</ul>`);
            htmlLines.push(`<br/>`);
            textLines.push('');
        }

        // 용어
        if (body.additional && body.additional.length > 0) {
            textLines.push(`용어`);
            htmlLines.push(`<h3 style="font-size: 18px; line-height: 1.8;">용어</h3>`);
            htmlLines.push(`<ul>`);
            _.forEach(body.additional, (additional) => {
                textLines.push(`- ${additional}`);
                htmlLines.push(`<li>${additional}</li>`);
            });
            htmlLines.push(`</ul>`);
            htmlLines.push(`<br/>`);
            textLines.push('');
        }

        // 대응 방안
        if (body.action && body.action.length > 0) {
            textLines.push(`대응 방안`);
            htmlLines.push(`<h3 style="font-size: 18px; line-height: 1.8;">대응 방안</h3>`);
            htmlLines.push(`<ul>`);
            _.forEach(body.action, (action) => {
                textLines.push(`- ${action}`);
                htmlLines.push(`<li>${action}</li>`);
            });
            htmlLines.push(`</ul>`);
            htmlLines.push(`<br/><br />`);
            textLines.push('');
        }

        // 해시태그 키워드
        if (this.newsSummary.keywords && this.newsSummary.keywords.length > 0) {
            const keywords = _.map(this.newsSummary.keywords, (keyword) => `#${keyword}`).join(' ');
            textLines.push(keywords);
            htmlLines.push(`<p>${keywords}</p>`);
        }

        const textContent = textLines.join('\n');
        const htmlContent = htmlLines.join('\n');

        try {
            // 텍스트와 HTML을 모두 클립보드에 복사
            await navigator.clipboard.write([
                new ClipboardItem({
                    'text/plain': new Blob([textContent], { type: 'text/plain' }),
                    'text/html': new Blob([htmlContent], { type: 'text/html' }),
                }),
            ]);

            toastStore.success('본문이 복사되었습니다');
        } catch (error) {
            toastStore.error('복사에 실패했습니다');
        }
    }
}

const NewsDetailPage = observer(() => {
    const { newsSummarySlug } = useParams();
    const { navigateBack } = useCommonNavigate();
    const viewStore = useMobxInstance(ViewStore, newsSummarySlug as string);

    useSsr(
        'NewsDetailPage',
        {
            newsSummarySlug: newsSummarySlug as string,
        },
        viewStore,
    );

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    const newsSummary = viewStore.newsSummary;

    return (
        <Frame>
            {newsSummary && (
                <>
                    <MetaHeader
                        title={newsSummary?.title}
                        description={newsSummary?.body.explanation}
                    />
                    <Jsonld
                        type="BlogPosting"
                        title={newsSummary?.title}
                        description={newsSummary?.body.explanation || ''}
                        publishedAt={newsSummary?.publishedAt}
                    />
                    <Frame.Title
                        title={newsSummary.title}
                        isShowMoreButton={firebaseStore.isAdmin}
                        onClickMoreButton={async () => {
                            await modalStore.menu({
                                title: '더보기',
                                items: [
                                    {
                                        name: '뉴스 복사',
                                        icon: <CopyIcon className="size-4" />,
                                        onSelect: async ({ modalId }) => {
                                            await modalStore.close(modalId);

                                            await modalStore.custom({
                                                title: '뉴스 복사',
                                                renderComponent: () => {
                                                    return (
                                                        <NewsCopyModal
                                                            onCopyTitle={async () => {
                                                                await viewStore.copyTitle();
                                                            }}
                                                            onCopyContent={async () => {
                                                                await viewStore.copyContentForBlog();
                                                            }}
                                                        />
                                                    );
                                                },
                                            });
                                        },
                                    },
                                    {
                                        name: '삭제',
                                        icon: <Trash2Icon className="size-4" />,
                                        onSelect: async ({ modalId }) => {
                                            await modalStore.close(modalId);

                                            const result = await modalStore.confirm({
                                                message: '정말 삭제하시겠습니까?',
                                                confirmStyle: 'danger',
                                            });
                                            if (result.returnValue) {
                                                const deleted =
                                                    await apiClient.news.admin.deleteNewsSummary.mutate(
                                                        {
                                                            newsSummarySlug: newsSummarySlug!,
                                                        },
                                                    );

                                                if (deleted) {
                                                    if (webViewStore.isWebView) {
                                                        await webViewStore.syncUi(
                                                            'newsSummaryDeleted',
                                                            {
                                                                newsSummarySlug: newsSummarySlug!,
                                                            },
                                                        );
                                                    } else {
                                                        apiClient.news.public.getNewsSummaryList.$invalidate();
                                                    }

                                                    navigateBack();
                                                } else {
                                                    modalStore.alert({
                                                        message: '삭제에 실패했습니다.',
                                                    });
                                                }
                                            }
                                        },
                                    },
                                ],
                            });
                        }}
                    />
                    <Frame.Description>
                        <CommonTimeFormat
                            time={newsSummary.publishedAt}
                            isShowHour={true}
                            fromNow={false}
                        />
                    </Frame.Description>
                    <Box noBorder={true} className="mt-2 mb-5 gap-0 bg-transparent py-0">
                        <h3 className="text-base-content/60 text-sm">핵심요약</h3>
                        <div className="mt-2 text-lg leading-[1.6] font-medium">
                            {viewStore?.newsSummary?.body.explanation}
                        </div>
                    </Box>
                    {_.isEmpty(newsSummary.tickers) ? null : (
                        <Box>
                            <Box.Title title="관련 종목" />
                            <div className="flex flex-row flex-wrap gap-2">
                                {_.map(newsSummary.tickers, (ticker) => (
                                    <button
                                        className="badge badge-soft badge-md rounded-sm font-semibold"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            modalStore.stock({
                                                ticker,
                                            });
                                        }}
                                    >
                                        {ticker}
                                        <ChevronDownIcon className="size-3" />
                                    </button>
                                ))}
                            </div>
                        </Box>
                    )}
                    <Box>
                        <MarkdownViewer markdown={viewStore.markdown} className="mt-3.5" />
                        <div className="mt-10 mb-2 flex flex-row flex-wrap gap-2">
                            {newsSummary.keywords?.map((keyword) => (
                                <div
                                    key={keyword}
                                    className="badge badge-ghost badge-md link link-hover rounded-none px-2 py-3 text-sm"
                                >
                                    #{keyword}
                                </div>
                            ))}
                        </div>
                    </Box>
                </>
            )}
        </Frame>
    );
});

export default NewsDetailPage;
